// Core
import React, { useMemo } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Types
import { MeasurementType, TimeUnitType } from '../../../../../../types/global';

// Components
import { BarChart, Series, SeriesColorEnum } from '../../../../../../layout/elements/BarChart';

// Component
import { CoastDownProps } from './CoastDown.types';

function CoastDown({
  className,
  testId,
  unitData,
}: CoastDownProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  // Configuration for the single series for the categorical bar chart
  const series: Series[] = useMemo(() => [
    {
      name: 'Coast Down Time',
      field: 'value',
      decimals: 2,
      unit: TimeUnitType.minute,
      color: SeriesColorEnum.lightBlue,
      measurementType: MeasurementType.time,
      yAxisMax: 180000, // 3 minutes
      yAxisMin: 0,
    },
  ], []);

  /**
   * Transform the data into a categorical structure for the bar chart.
   * Each coast down event becomes a distinct category in the chart.
   */
  const processedData: Record<string, unknown>[] = useMemo(() => {
    // If unitData is null or undefined, return an empty array
    if (!unitData) {
      return [];
    }

    // Auxiliary function to calculate the total seconds
    const calculateTotalSeconds = (
      minutesKey: string,
      secondsKey: string,
      dataObject: Record<string, any>,
    ): number | null => {
      let totalSeconds: number | null = null;
      const minutes: number | undefined = dataObject[minutesKey];
      const seconds: number | undefined = dataObject[secondsKey];

      if (typeof minutes === 'number' && typeof seconds === 'number') {
        totalSeconds = ((minutes * 60) + seconds) * 1000;
      }

      return totalSeconds;
    };

    // Define the categories and their respective data fields
    const eventCategories: { category: string; minutesKey: string; secondsKey: string }[] = [
      {
        category: t('common.lastEvent'),
        minutesKey: 'coastDownTimeMinutesLast',
        secondsKey: 'coastDownTimeSecondsLast',
      },
      {
        category: 'E -1',
        minutesKey: 'coastDownTimeMinutesArray8',
        secondsKey: 'coastDownTimeSecondsArray8',
      },
      {
        category: 'E -2',
        minutesKey: 'coastDownTimeMinutesArray7',
        secondsKey: 'coastDownTimeSecondsArray7',
      },
      {
        category: 'E -3',
        minutesKey: 'coastDownTimeMinutesArray6',
        secondsKey: 'coastDownTimeSecondsArray6',
      },
      {
        category: 'E -4',
        minutesKey: 'coastDownTimeMinutesArray5',
        secondsKey: 'coastDownTimeSecondsArray5',
      },
      {
        category: 'E -5',
        minutesKey: 'coastDownTimeMinutesArray4',
        secondsKey: 'coastDownTimeSecondsArray4',
      },
      {
        category: 'E -6',
        minutesKey: 'coastDownTimeMinutesArray3',
        secondsKey: 'coastDownTimeSecondsArray3',
      },
      {
        category: 'E -7',
        minutesKey: 'coastDownTimeMinutesArray2',
        secondsKey: 'coastDownTimeSecondsArray2',
      },
      {
        category: 'E -8',
        minutesKey: 'coastDownTimeMinutesArray1',
        secondsKey: 'coastDownTimeSecondsArray1',
      },
      {
        category: 'E -9',
        minutesKey: 'coastDownTimeMinutesArray0',
        secondsKey: 'coastDownTimeSecondsArray0',
      },
    ];

    // Map each category to a data object for the chart
    return eventCategories.map(({ category, minutesKey, secondsKey }) => ({
      category,
      value: calculateTotalSeconds(minutesKey, secondsKey, unitData),
    }));
  }, [unitData, t]);

  return (
    <BarChart
      className={className}
      data={processedData}
      id="coast-down-chart"
      series={series}
      testId={testId}
    />
  );
}

export { CoastDown };
